import React, { useCallback, useState, useRef, useEffect } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { Button } from "@/components";
import { cn, layout, spacing, typography } from "@/utilities/tailwind-utils";
import { config } from "@/config";

// Map container styles
const mapContainerStyle = {
  width: "100%",
  height: "400px"
};

// Default center (can be overridden by props)
const defaultCenter = {
  lat: 45.4215,
  lng: -75.6997 // Ottawa, Canada
};

export interface MapLocation {
  lat: number;
  lng: number;
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

function parseGeocoderResult(
  result: google.maps.GeocoderResult
): Pick<
  MapLocation,
  "address" | "street" | "city" | "state" | "postalCode" | "country"
> {
  let streetNumber = "";
  let route = "";
  let city = "";
  let state = "";
  let postalCode = "";
  let country = "";

  for (const component of result.address_components) {
    const types = component.types;
    if (types.includes("street_number")) {
      streetNumber = component.long_name;
    } else if (types.includes("route")) {
      route = component.long_name;
    } else if (types.includes("locality") && !city) {
      city = component.long_name;
    } else if (types.includes("postal_town") && !city) {
      city = component.long_name;
    } else if (types.includes("sublocality_level_1") && !city) {
      city = component.long_name;
    } else if (types.includes("administrative_area_level_2") && !city) {
      city = component.long_name;
    } else if (types.includes("administrative_area_level_1")) {
      state = component.short_name || component.long_name;
    } else if (types.includes("postal_code")) {
      postalCode = component.long_name;
    } else if (types.includes("country")) {
      country = component.long_name;
    }
  }

  const street = [streetNumber, route].filter(Boolean).join(" ").trim();

  return {
    address: result.formatted_address,
    street: street || undefined,
    city: city || undefined,
    state: state || undefined,
    postalCode: postalCode || undefined,
    country: country || undefined
  };
}

export interface MapLocationSelectorProps {
  center?: { lat: number; lng: number };
  onLocationSelect: (location: MapLocation) => void;
  onClose: () => void;
  initialLocation?: MapLocation | null;
}

export const MapLocationSelector: React.FC<MapLocationSelectorProps> = ({
  center = defaultCenter,
  onLocationSelect,
  onClose,
  initialLocation
}) => {
  const [selectedLocation, setSelectedLocation] = useState<MapLocation | null>(
    initialLocation || null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-maps-script",
    googleMapsApiKey: config.maps.apiKey,
    libraries: ["places", "geocoding"]
  });

  // Initialize geocoder when map is loaded
  useEffect(() => {
    if (isLoaded && !geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
  }, [isLoaded]);

  // Handle map click to select location
  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const newLocation: MapLocation = { lat, lng };
      setSelectedLocation(newLocation);
      setError(null);
    }
  }, []);

  // Geocode the selected location to get address details
  const geocodeLocation = useCallback(
    async (location: MapLocation): Promise<MapLocation> => {
      if (!geocoderRef.current) return location;

      try {
        const result = await geocoderRef.current.geocode({
          location: { lat: location.lat, lng: location.lng }
        });

        if (result.results && result.results.length > 0) {
          return {
            ...location,
            ...parseGeocoderResult(result.results[0])
          };
        }
        return location;
      } catch (error) {
        console.error("Geocoding error:", error);
        return location;
      }
    },
    []
  );

  // Handle location selection with geocoding
  const handleSelectLocation = useCallback(async () => {
    if (!selectedLocation) return;

    setIsLoading(true);
    setError(null);

    try {
      const locationWithAddress = await geocodeLocation(selectedLocation);
      onLocationSelect(locationWithAddress);
    } catch (error) {
      setError("Failed to get address for selected location");
      console.error("Location selection error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocation, geocodeLocation, onLocationSelect]);

  // Handle map load
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  // Handle map unload
  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  if (loadError) {
    return (
      <div className={cn(layout.flexCenter, spacing.p4)}>
        <div className={cn(layout.flexCenter, "flex-col", spacing.gap2)}>
          <p className={cn(typography.errorMessage)}>
            Failed to load Google Maps. Please check your API key.
          </p>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={cn(layout.flexCenter, spacing.p4)}>
        <div className={cn(layout.flexCenter, "flex-col", spacing.gap2)}>
          <p>Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4")}>
      {/* Map */}
      <div className="relative">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={selectedLocation || center}
          zoom={15}
          onClick={handleMapClick}
          onLoad={onMapLoad}
          onUnmount={onMapUnmount}
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true
          }}
        >
          {selectedLocation && (
            <Marker
              position={{
                lat: selectedLocation.lat,
                lng: selectedLocation.lng
              }}
              draggable={true}
              onDragEnd={(event) => {
                if (event.latLng) {
                  const lat = event.latLng.lat();
                  const lng = event.latLng.lng();
                  setSelectedLocation({ lat, lng });
                }
              }}
            />
          )}
        </GoogleMap>
      </div>

      {/* Instructions */}
      <div className={cn(spacing.p4, "bg-gray-50 rounded-lg")}>
        <p className={cn(typography.textSm, "text-gray-600")}>
          Click on the map to select a location or drag the marker to adjust the
          position.
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div
          className={cn(
            spacing.p3,
            "bg-red-50 border border-red-200 rounded-lg"
          )}
        >
          <p className={cn(typography.errorMessage)}>{error}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className={cn(layout.flexCenter, spacing.gap4)}>
        <Button
          onClick={handleSelectLocation}
          disabled={!selectedLocation || isLoading}
          className="flex-1"
        >
          {isLoading ? "Getting Address..." : "Select Location"}
        </Button>
        <Button onClick={onClose} variant="outline" className="flex-1">
          Cancel
        </Button>
      </div>

      {/* Selected location info */}
      {selectedLocation && (
        <div
          className={cn(
            spacing.p3,
            "bg-blue-50 border border-blue-200 rounded-lg"
          )}
        >
          <p className={cn(typography.textSm, "font-medium text-blue-800")}>
            Selected Location:
          </p>
          <p className={cn(typography.textSm, "text-blue-600")}>
            Lat: {selectedLocation.lat.toFixed(6)}, Lng:{" "}
            {selectedLocation.lng.toFixed(6)}
          </p>
        </div>
      )}
    </div>
  );
};
